// Sends the visitor's cookie-consent choice plus the analytics/ad cookie ids to our own
// backend in one X-Trk header, so a Stripe checkout or a new account can carry a consent
// snapshot for server-side conversions. Ids are only included when the matching consent
// is granted (GA4 client id: analytics; Meta/TikTok ids: marketing). No choice = no header.
import axios from "axios";
import { getConsent, CONSENT_EVENT } from "@/lib/consent";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CLICK_KEY = "samtwo_click_ids";

function cookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

// Ad click ids arrive once on the landing URL; keep them for this tab session only.
function captureClickIds() {
  try {
    const p = new URLSearchParams(window.location.search);
    const prev = JSON.parse(sessionStorage.getItem(CLICK_KEY) || "{}");
    const next = { ...prev };
    if (p.get("fbclid")) next.fbclid = { v: p.get("fbclid"), t: Date.now() };
    if (p.get("ttclid")) next.ttclid = p.get("ttclid");
    sessionStorage.setItem(CLICK_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

function clickIds() {
  try { return JSON.parse(sessionStorage.getItem(CLICK_KEY) || "{}"); } catch { return {}; }
}

// "GA1.1.123456789.1700000000" -> "123456789.1700000000"
export function gaClientId() {
  const ga = cookie("_ga");
  const parts = ga ? ga.split(".") : [];
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
}

export function trackingSnapshot() {
  const c = getConsent();
  if (!c) return null;
  const snap = { ts: c.timestamp, a: c.analytics ? 1 : 0, m: c.marketing ? 1 : 0, url: window.location.href.split("#")[0] };
  if (c.analytics) snap.cid = gaClientId();
  if (c.marketing) {
    const ids = clickIds();
    snap.fbp = cookie("_fbp");
    snap.fbc = cookie("_fbc") || (ids.fbclid ? `fb.1.${ids.fbclid.t}.${ids.fbclid.v}` : null);
    snap.ttp = cookie("_ttp");
    snap.ttclid = ids.ttclid || null;
  }
  return snap;
}

function encode(obj) {
  const json = JSON.stringify(obj);
  return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function isOwnApi(cfg) {
  const url = cfg.baseURL ? `${cfg.baseURL}${cfg.url || ""}` : cfg.url || "";
  return !!BACKEND && url.startsWith(BACKEND);
}

export function attachTrackingHeader(client) {
  client.interceptors.request.use((cfg) => {
    try {
      const snap = isOwnApi(cfg) ? trackingSnapshot() : null;
      if (snap) cfg.headers["X-Trk"] = encode(snap);
    } catch { /* never block a request */ }
    return cfg;
  });
}

// Store the latest choice on the logged-in account so renewals/cancels honour it.
export function syncConsentToAccount() {
  const token = localStorage.getItem("samtwo_platform_token");
  if (!token || !getConsent()) return;
  axios.post(`${BACKEND}/api/me/tracking-consent`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
}

export function initTrackingContext() {
  captureClickIds();
  attachTrackingHeader(axios);
  window.addEventListener(CONSENT_EVENT, syncConsentToAccount);
  try {
    if (!sessionStorage.getItem("samtwo_consent_synced")) {
      sessionStorage.setItem("samtwo_consent_synced", "1");
      syncConsentToAccount();
    }
  } catch { /* ignore */ }
}
